"use client";

import { useInView, useMotionValue, useSpring } from "motion/react";
import { ComponentPropsWithoutRef, useEffect, useRef } from "react";
import { useLocale } from "next-intl";

import { cn } from "@/lib/utils";

interface NumberTickerProps extends ComponentPropsWithoutRef<"span"> {
    value: number;
    startValue?: number;
    direction?: "up" | "down";
    delay?: number;
    decimalPlaces?: number;
    locale?: string;
}

export function NumberTicker({
    value,
    startValue = 0,
    direction = "up",
    delay = 0,
    className,
    decimalPlaces = 0,
    locale,
    ...props
}: NumberTickerProps) {
    const ref = useRef<HTMLSpanElement>(null);
    const contextLocale = useLocale();
    const resolvedLocale = locale ?? contextLocale ?? "fr-FR";
    const motionValue = useMotionValue(
        direction === "down" ? value : startValue,
    );
    const springValue = useSpring(motionValue, {
        damping: 60,
        stiffness: 100,
    });
    const isInView = useInView(ref, { once: true, margin: "0px" });

    useEffect(() => {
        if (isInView) {
            const timer = setTimeout(() => {
                motionValue.set(direction === "down" ? startValue : value);
            }, delay * 1000);
            return () => clearTimeout(timer);
        }
    }, [motionValue, isInView, delay, value, direction, startValue]);

    useEffect(
        () =>
            springValue.on("change", (latest) => {
                if (ref.current) {
                    ref.current.textContent = Intl.NumberFormat(
                        resolvedLocale,
                        {
                            minimumFractionDigits: decimalPlaces,
                            maximumFractionDigits: decimalPlaces,
                        },
                    ).format(Number(latest.toFixed(decimalPlaces)));
                }
            }),
        [springValue, decimalPlaces, resolvedLocale],
    );

    const formattedStartValue = Intl.NumberFormat(resolvedLocale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(Number(startValue.toFixed(decimalPlaces)));

    return (
        <span
            ref={ref}
            className={cn(
                "inline-block tabular-nums tracking-wider text-black dark:text-white",
                className,
            )}
            {...props}
        >
            {formattedStartValue}
        </span>
    );
}
